//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import { PojoMap } from 'pojo-maps';
import React, { useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTarget } from 'unison';

import { generateOnePageLink } from '@apis/dynamic-landing-pages/dynamicLandingPagesApi';
import useSelfDestructingMessage from '@hooks/useSelfDestructingMessage';
import Cuid from '@models/Cuid';
import styled from 'styled-components';
import copyToClipBoard from '@utils/copyToClipboard';
import { setFeatureValue } from '@utils/featureStorage';
import { FeatureStorageScope } from '@utils/featureStorageModel';
import gaApi from 'app/initialization/ga/ga-api';

const TRACKING_CATEGORY = gaApi.CATEGORY_ONE_PAGE;

export enum OnePageLoadingState {
  Waiting = 'waiting',
  Loading = 'loading',
  Copied = 'copied',
}

interface IOwnProps {
  // If true, load the one page URL when the component is
  // initialized rather than waiting until the user tries
  // to copy it
  preload?: boolean;
  renderTooltip?: (url: string) => React.ReactElement;
  renderCopyElement: (
    generateUrl: (event: React.MouseEvent) => Promise<void>,
    loadingState: OnePageLoadingState,
    url: string,
  ) => React.ReactElement;
}

export interface IOnePageProps extends React.HTMLAttributes<HTMLDivElement> {
  cuid: Cuid;
  onCopyClicked?: () => void;
  onCopyComplete?: (url: string) => void;
}

interface Props extends IOwnProps, IOnePageProps {}

const OnePageTemplate = (props: Props) => {
  const {
    cuid,
    preload,
    renderCopyElement,
    renderTooltip,
    onCopyClicked,
    onCopyComplete,
  } = props;

  const COPIED_TOOLTIP_TIMEOUT = 3000;
  const RETURN_TO_WAITING_STATE_TIMEOUT = 2000;

  const [onePageUrl, setOnePageUrl] = useState<string | undefined>(undefined);
  const [loadingState, setLoadingState] = useState<OnePageLoadingState>(
    preload ? OnePageLoadingState.Loading : OnePageLoadingState.Waiting,
  );
  const [isUrlCopiedShown, triggerShowUrlCopied] = useSelfDestructingMessage(
    COPIED_TOOLTIP_TIMEOUT,
  );

  useEffect(() => {
    if (preload) {
      fetchOnePageUrl().then(() => {
        setLoadingState(OnePageLoadingState.Waiting);
      });
    }
  }, []);

  const fetchOnePageUrl = async () => {
    if (onePageUrl === undefined) {
      const onePageRes = await generateOnePageLink([cuid]);
      const url = PojoMap.get(onePageRes.urlsByCuid, cuid)!;
      if (preload) {
        gaApi.recordEvent(
          TRACKING_CATEGORY,
          gaApi.ACTION_ONE_PAGE_PRELOAD_LINK,
        );
      } else {
        gaApi.recordEvent(
          TRACKING_CATEGORY,
          gaApi.ACTION_ONE_PAGE_GENERATE_LINK,
        );
      }
      setOnePageUrl(url);
    }
  };

  const copyLink = async () => {
    event.preventDefault();
    event.stopPropagation();

    if (onCopyClicked) {
      onCopyClicked();
    }

    setLoadingState(OnePageLoadingState.Loading);

    await fetchOnePageUrl();

    copyToClipBoard(onePageUrl);
    setLoadingState(OnePageLoadingState.Copied);

    gaApi.recordEvent(TRACKING_CATEGORY, gaApi.ACTION_ONE_PAGE_COPY_LINK);

    triggerShowUrlCopied();
    setFeatureValue(FeatureStorageScope.COPIED_ONE_PAGE_LINK, { value: true });
    if (onCopyComplete) {
      onCopyComplete(onePageUrl);
    }
    window.setTimeout(
      () => setLoadingState(OnePageLoadingState.Waiting),
      RETURN_TO_WAITING_STATE_TIMEOUT,
    );
  };

  return (
    <Popover
      isOpen={isUrlCopiedShown}
      toggle={() => undefined}
      positioningStrategy={{
        fixed: 'bottom',
        align: 'center',
      }}
    >
      <FillPopoverTarget allowCustomOnClick={true}>
        {renderCopyElement(copyLink, loadingState, onePageUrl)}
      </FillPopoverTarget>
      {renderTooltip && (
        <PopoverContent>{renderTooltip(onePageUrl)}</PopoverContent>
      )}
    </Popover>
  );
};

const FillPopoverTarget = styled(PopoverTarget)`
  width: 100%;
`;

export default OnePageTemplate;
